import { useMemo } from 'react'
import type { TransitGraphData } from '@/contexts/EventDataContext'

interface TransitGraphProps {
  data: TransitGraphData
}

function formatDuration(secs: number): string {
  if (secs < 60) return `${Math.round(secs)}s`
  const mins = Math.floor(secs / 60)
  return mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`
}

export function TransitGraph({ data }: TransitGraphProps) {
  const { nodes, edges } = data

  const { scaledNodes, maxRevenue } = useMemo(() => {
    const mt = Math.max(...nodes.map(n => n.total_scans), 1)
    const mr = Math.max(...nodes.map(n => n.revenue), 1)
    const scaled = nodes.map(n => ({
      ...n,
      radius: 8 + (n.total_scans / mt) * 20,
    }))
    return { scaledNodes: scaled, maxRevenue: mr }
  }, [nodes])

  const maxObs = useMemo(() => Math.max(...edges.map(e => e.observations), 1), [edges])

  if (nodes.length === 0) {
    return (
      <div className="mm-card-v3 p-6 text-center text-sm text-ink-soft">
        No transit data available for this event.
      </div>
    )
  }

  const viewBox = '0 0 500 500'

  return (
    <div className="mm-card-v3 p-5">
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-2 text-xs text-ink-soft">
          <span className="inline-block w-3 h-3 rounded-full bg-butler-600" />
          <span>Rides</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-ink-soft">
          <span className="inline-block w-3 h-3 rounded-full bg-amber-500" />
          <span>Games</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-ink-soft">
          <span className="inline-block w-3 h-3 rounded-full bg-green-500" />
          <span>Food</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-ink-soft">
          <span className="inline-block w-3 h-3 rounded-full bg-slate-400" />
          <span>Other</span>
        </div>
        <div className="ml-auto text-[10px] text-ink-soft">
          Size = traffic · Color = type · Line thickness = observations
        </div>
      </div>

      <svg viewBox={viewBox} className="w-full max-h-[500px]" role="img" aria-label="Transit graph showing attraction positions and connections">
        {/* Edges */}
        {edges.slice(0, 80).map((e, i) => {
          const from = scaledNodes.find(n => n.id === e.from)
          const to = scaledNodes.find(n => n.id === e.to)
          if (!from || !to) return null
          const thickness = 0.5 + (e.observations / maxObs) * 4
          return (
            <line
              key={i}
              x1={from.x}
              y1={from.y}
              x2={to.x}
              y2={to.y}
              stroke="rgba(0,112,171,0.15)"
              strokeWidth={thickness}
            >
              <title>{`${e.from_name} → ${e.to_name}: ${Math.round(e.distance_m)}m, ${formatDuration(e.median_sec)}, ${e.observations} obs`}</title>
            </line>
          )
        })}

        {/* Nodes */}
        {scaledNodes.map(n => {
          const fill = n.type === 'Rides' ? '#0070ab'
            : n.type === 'Games' ? '#f59e0b'
            : n.type === 'Food' || n.type === 'Food Stand' ? '#22c55e'
            : '#94a3b8'
          const intensity = 0.3 + (n.revenue / maxRevenue) * 0.7
          return (
            <g key={n.id}>
              <circle
                cx={n.x}
                cy={n.y}
                r={n.radius}
                fill={fill}
                opacity={intensity}
                stroke="white"
                strokeWidth={1.5}
              >
                <title>{`${n.name} (${n.type}): ${n.total_scans.toLocaleString()} scans, $${n.revenue.toLocaleString()} revenue`}</title>
              </circle>
              {n.radius > 14 && (
                <text
                  x={n.x}
                  y={n.y + n.radius + 12}
                  textAnchor="middle"
                  className="text-[8px] fill-ink-soft"
                >
                  {n.name.length > 15 ? n.name.slice(0, 14) + '…' : n.name}
                </text>
              )}
            </g>
          )
        })}
      </svg>

      <div className="flex justify-between mt-3 text-[10px] text-ink-soft">
        <span>{data.node_count} attractions</span>
        <span>{data.edge_count} connections</span>
      </div>
    </div>
  )
}
